import type { CSSProperties, MouseEvent, ReactNode } from 'react'
import { ChevronUpIcon, ChevronDownIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline'
import type { SortDirection } from '@/hooks/useSortableTable'
import ColumnResizeHandle from '@/components/ColumnResizeHandle'

interface SortableHeaderProps {
  label: ReactNode
  sortKey: string
  sortDirection: SortDirection | null
  onSort: (key: string) => void
  className?: string
  title?: string
  /** When set with onColumnResizeStart, column width can be dragged from the right edge. */
  columnKey?: string
  width?: number
  onColumnResizeStart?: (e: MouseEvent, columnKey: string, currentWidth: number) => void
  style?: CSSProperties
}

export default function SortableHeader({
  label,
  sortKey,
  sortDirection,
  onSort,
  className = '',
  title,
  columnKey,
  width,
  onColumnResizeStart,
  style,
}: SortableHeaderProps) {
  const isSorted = sortDirection !== null
  const resizable =
    columnKey !== undefined && onColumnResizeStart !== undefined && width !== undefined

  const mergedStyle: CSSProperties | undefined =
    resizable || style !== undefined
      ? {
          ...style,
          ...(resizable
            ? {
                width,
                minWidth: width,
                maxWidth: width,
                position: (style?.position as CSSProperties['position']) ?? 'sticky',
                top: style?.top ?? 0,
                left: style?.left,
                zIndex: style?.zIndex ?? 10,
                borderRight: style?.borderRight,
                boxSizing: 'border-box' as const,
              }
            : {}),
        }
      : undefined

  return (
    <th
      className={`sortable ${isSorted ? 'sorted' : ''} ${className}`}
      style={mergedStyle}
      title={title}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1 pr-2">
        {label}
        <span className="sort-indicator">
          {sortDirection === 'asc' ? (
            <ChevronUpIcon className="w-3 h-3" />
          ) : sortDirection === 'desc' ? (
            <ChevronDownIcon className="w-3 h-3" />
          ) : (
            <ChevronUpDownIcon className="w-3 h-3" />
          )}
        </span>
      </span>
      {resizable && (
        <ColumnResizeHandle
          onResizeMouseDown={(e) => onColumnResizeStart(e, columnKey, width)}
        />
      )}
    </th>
  )
}
